import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DefaultService, UsageCount, UsageEventRecord, UsageSession, UsageSummary } from '../generated-api';
import PageHeader from '../components/common/PageHeader';
import { decodeReplayChunks } from '../services/replayDecode';
import 'rrweb-player/dist/style.css';

const card = 'bg-surface rounded-lg border border-edge p-4';

const CountTable: React.FC<{ title: string; rows: UsageCount[]; valueLabel?: string; empty?: string }> = ({
  title,
  rows,
  valueLabel,
  empty = 'Nothing yet',
}) => (
  <div className={card}>
    <h2 className="font-semibold text-heading mb-2">{title}</h2>
    {rows.length === 0 ? (
      <p className="text-sm text-muted">{empty}</p>
    ) : (
      <table className="w-full text-sm">
        <tbody>
          {rows.map(r => (
            <tr key={r.key} className="border-t border-edge first:border-t-0">
              <td className="py-1 pr-2 break-all text-body">{r.key}</td>
              <td className="py-1 text-right tabular-nums text-heading">{r.count}</td>
              {valueLabel && (
                <td className="py-1 pl-3 text-right tabular-nums text-muted">
                  {r.value != null ? `${Math.round(r.value * 100) / 100} ${valueLabel}` : ''}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const Bars: React.FC<{ title: string; rows: UsageCount[] }> = ({ title, rows }) => {
  const max = Math.max(1, ...rows.map(r => r.count));
  return (
    <div className={card}>
      <h2 className="font-semibold text-heading mb-2">{title}</h2>
      {rows.length === 0 ? (
        <p className="text-sm text-muted">Nothing yet</p>
      ) : (
        <div className="flex items-end gap-1 h-28" data-testid={`bars-${title}`}>
          {rows.map(r => (
            <div key={r.key} className="flex-1 flex flex-col items-center justify-end h-full" title={`${r.key}: ${r.count}`}>
              <div className="w-full bg-primary-500 rounded-t" style={{ height: `${(r.count / max) * 100}%` }} />
              <div className="text-[10px] text-muted mt-1 truncate w-full text-center">{r.key.slice(5)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ReplayViewer: React.FC<{ sessionId: string; onClose: () => void }> = ({ sessionId, onClose }) => {
  const target = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState('Loading replay…');

  useEffect(() => {
    let player: any = null;
    let cancelled = false;
    (async () => {
      try {
        const replay = await DefaultService.getSessionReplay({ sessionId });
        const events = await decodeReplayChunks(replay.chunks);
        if (cancelled) return;
        if (events.length < 2) {
          setStatus('This session has no replay data.');
          return;
        }
        const { default: RrwebPlayer } = await import('rrweb-player');
        if (cancelled || !target.current) return;
        target.current.innerHTML = '';
        const width = Math.min(target.current.clientWidth || 800, 1000);
        player = new RrwebPlayer({
          target: target.current,
          props: { events, width, height: Math.round(width * 0.6), autoPlay: true, showController: true },
        });
        setStatus('');
      } catch (err: any) {
        setStatus(`Could not load replay: ${err?.message || err}`);
      }
    })();
    return () => {
      cancelled = true;
      try {
        player?.$destroy?.();
      } catch {
        // ignore
      }
    };
  }, [sessionId]);

  return (
    <div className={`${card} telemetry-block`}>
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-semibold text-heading">Replay {sessionId}</h2>
        <button type="button" onClick={onClose} className="text-sm text-muted underline">
          Close
        </button>
      </div>
      {status && <p className="text-sm text-muted">{status}</p>}
      <div ref={target} data-testid="replay-target" />
    </div>
  );
};

const UsagePage: React.FC = () => {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [sessions, setSessions] = useState<UsageSession[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [player, setPlayer] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<UsageEventRecord[]>([]);
  const [replay, setReplay] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [s, list] = await Promise.all([
        DefaultService.getUsageSummary({ days }),
        DefaultService.listUsageSessions({ days: Math.min(days, 30) }),
      ]);
      setSummary(s);
      setSessions(list);
    } catch (err: any) {
      setError(
        err?.status === 404
          ? 'Usage telemetry is not deployed yet (run the Terraform apply in the README).'
          : 'Failed to load usage data',
      );
    }
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!player) return;
    DefaultService.getPlayerUsageEvents({ email: player, limit: 200 })
      .then(setTimeline)
      .catch(() => setTimeline([]));
  }, [player]);

  return (
    <div className="space-y-4 pb-8">
      <PageHeader className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-heading">Usage</h1>
        <select
          value={days}
          onChange={e => setDays(Number(e.target.value))}
          className="rounded-md border border-edge bg-surface text-sm px-2 py-1"
          aria-label="Period"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </PageHeader>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {replay && <ReplayViewer sessionId={replay} onClose={() => setReplay(null)} />}

      {summary && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className={card}>
              <div className="text-xs text-muted">Events</div>
              <div className="text-2xl font-bold text-heading" data-testid="usage-total-events">{summary.total_events}</div>
            </div>
            <div className={card}>
              <div className="text-xs text-muted">Active players</div>
              <div className="text-2xl font-bold text-heading">{summary.players.length}</div>
            </div>
            <div className={card}>
              <div className="text-xs text-muted">Sessions</div>
              <div className="text-2xl font-bold text-heading">{sessions.length}</div>
            </div>
            <div className={card}>
              <div className="text-xs text-muted">Errors</div>
              <div className="text-2xl font-bold text-heading">
                {summary.errors.reduce((n, e) => n + e.count, 0)}
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <Bars title="Daily active players" rows={summary.daily_active} />
            <Bars title="Weekly active players" rows={summary.weekly_active} />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div className={card}>
              <h2 className="font-semibold text-heading mb-2">Players</h2>
              {summary.players.length === 0 && <p className="text-sm text-muted">Nothing yet</p>}
              <ul className="text-sm divide-y divide-edge">
                {summary.players.map(p => (
                  <li key={p.key} className="py-1 flex justify-between">
                    <button data-track="usage_player" type="button" className="underline text-left break-all" onClick={() => setPlayer(p.key)}>
                      {p.key}
                    </button>
                    <span className="tabular-nums">{p.count}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className={card}>
              <h2 className="font-semibold text-heading mb-2">Funnel (sessions)</h2>
              {summary.funnel.map((f, i) => {
                const first = summary.funnel[0]?.count || 0;
                const pct = first ? Math.round((f.count / first) * 100) : 0;
                return (
                  <div key={f.key} className="mb-2">
                    <div className="flex justify-between text-sm">
                      <span>
                        {i + 1}. {f.key}
                      </span>
                      <span className="tabular-nums">
                        {f.count} ({pct}%)
                      </span>
                    </div>
                    <div className="h-2 bg-surface-active rounded">
                      <div className="h-2 bg-primary-500 rounded" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {player && (
            <div className={card}>
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-semibold text-heading">Timeline: {player}</h2>
                <button type="button" className="text-sm text-muted underline" onClick={() => setPlayer(null)}>
                  Close
                </button>
              </div>
              <div className="max-h-80 overflow-y-auto text-xs font-mono">
                {timeline.map((e, i) => (
                  <div key={`${e.ts}-${i}`} className="py-0.5 border-t border-edge first:border-t-0">
                    <span className="text-muted">{new Date(e.ts).toLocaleString()}</span> <b>{e.type}</b> {e.name}
                    {e.value != null && <span className="text-muted"> ({Math.round(e.value)})</span>}
                  </div>
                ))}
                {timeline.length === 0 && <p className="text-muted">No events</p>}
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <CountTable title="Top pages" rows={summary.top_pages} />
            <CountTable title="Top actions" rows={summary.top_actions} />
            <CountTable title="Errors" rows={summary.errors} empty="No errors" />
            <CountTable title="Slowest endpoints (p95)" rows={summary.slow_endpoints} valueLabel="ms" />
            <CountTable title="Web Vitals (p75)" rows={summary.vitals} valueLabel="" />
          </div>
        </>
      )}

      <div className={card}>
        <h2 className="font-semibold text-heading mb-2">Sessions</h2>
        {sessions.length === 0 ? (
          <p className="text-sm text-muted">No sessions yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted">
                  <th className="py-1 pr-2">Started</th>
                  <th className="py-1 pr-2">Player</th>
                  <th className="py-1 pr-2">Device</th>
                  <th className="py-1 pr-2 text-right">Events</th>
                  <th className="py-1" />
                </tr>
              </thead>
              <tbody>
                {sessions.map(s => (
                  <tr key={s.session_id} className="border-t border-edge">
                    <td className="py-1 pr-2 whitespace-nowrap">{new Date(s.started_at).toLocaleString()}</td>
                    <td className="py-1 pr-2 break-all">{s.email}</td>
                    <td className="py-1 pr-2">{s.device_type || ''}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{s.event_count}</td>
                    <td className="py-1 text-right">
                      {s.replay_chunks > 0 ? (
                        <button data-track="usage_replay_open" type="button" className="underline" onClick={() => setReplay(s.session_id)}>
                          Watch
                        </button>
                      ) : (
                        <span className="text-muted">no replay</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsagePage;
